// Framework-agnostic decision for the in-app /api/me identity bootstrap.
//
// The bug this guards (docs/REGRESSIONS.md): the SPA used to do
// `fetch('/api/me').then(r => r.json())` with NO `r.ok` check and only set the
// current user when `data.email` was truthy. A transient non-OK (especially a
// 5xx) or a thrown fetch/parse therefore silently produced a body with no
// email — so the avatar fell back to "U", the role downgraded to `user`, and
// the phone dock showed "Set up a number" — a FAILURE masquerading as a real
// signed-out / unprovisioned state.
//
// This pure function isolates the response-to-state DECISION so it can be
// unit-tested and shared by every /api/me reader. It does NOT fetch, retry, or
// touch React — the caller owns the retry/backoff loop and the setState calls.

/** Shape the SPA stores for the signed-in user (matches App.tsx `currentUser`). */
export interface MeUser {
  email: string
  name: string
  role: string
  userId: number | null
  isAdmin?: number
  hrProfileId?: number | null
  /** VERSIONED HR profile photo URL of the EFFECTIVE user (impersonation-aware
   *  — /api/me resolves the swapped identity). Feeds the top-nav avatar via
   *  navAvatarSource so an upload reflects on every route without waiting for
   *  the Preferences hrProfile load (docs/REGRESSIONS.md 2026-07-01). */
  photoUrl?: string | null
  /** Opaque per-upload photo version (cache-bust token for the by-email path). */
  photoVersion?: string | null
}

/** Loosely-typed parsed `/api/me` body (or null when parse/fetch failed). */
export type MeBody =
  | ({
      authenticated?: boolean
      email?: string | null
      name?: string | null
      role?: string | null
      userId?: number | null
      isAdmin?: number | null
      hrProfileId?: number | null
      photoUrl?: string | null
      photoVersion?: string | null
    } & Record<string, unknown>)
  | null

export type ResolveMeResult =
  // Adopt a freshly-resolved identity.
  | { action: 'set'; user: MeUser }
  // Server explicitly says the session is gone — the ONLY way we clear identity.
  | { action: 'signed-out' }
  // Transient/ambiguous response — keep whatever identity we already had.
  | { action: 'preserve'; user: MeUser | null }

/**
 * Decide what to do with a `/api/me` response.
 *
 * Precedence:
 *  1. An explicit `{ authenticated: false }` body → `signed-out`. `/api/me` is a
 *     public path that returns HTTP 200 with this body when the session is gone,
 *     so it is checked REGARDLESS of `httpOk`.
 *  2. `httpOk` AND a body carrying an `email` → `set` (adopt that identity).
 *  3. Anything else — a non-OK status (4xx/5xx without a signed-out body), or an
 *     OK-but-malformed/missing body — → `preserve` (never downgrade to "U" or
 *     role `user` on a blip; the caller retries first, then leaves prev intact).
 *
 * @param prevUser the user currently in state (echoed back on `preserve`)
 * @param httpOk    `response.ok`
 * @param body      parsed JSON body, or `null` if the fetch/parse threw
 */
export function resolveMeResponse(
  prevUser: MeUser | null,
  httpOk: boolean,
  body: MeBody,
): ResolveMeResult {
  // (1) Explicit signed-out — trust it even on a non-2xx.
  if (body && typeof body === 'object' && body.authenticated === false) {
    return { action: 'signed-out' }
  }

  // (2) A successful response that actually carries an identity.
  if (httpOk && body && typeof body === 'object' && body.email) {
    return {
      action: 'set',
      user: {
        email: String(body.email),
        name: String(body.name || ''),
        role: body.role || 'user',
        userId: body.userId || null,
        isAdmin: body.isAdmin === 1 ? 1 : 0,
        hrProfileId: body.hrProfileId ?? null,
        photoUrl: typeof body.photoUrl === 'string' && body.photoUrl ? body.photoUrl : null,
        photoVersion: body.photoVersion != null && body.photoVersion !== '' ? String(body.photoVersion) : null,
      },
    }
  }

  // (3) Transient non-OK or malformed/missing body — keep the prior identity.
  return { action: 'preserve', user: prevUser }
}
